import { useEffect, useState, useCallback } from 'react';

import { initChatbotParams, sendMessageToChatbot } from './iframeMessageHandler';
import { BI } from '../../constants';
import { useStore } from '../../zustand/stores';
import { CopilotDisplayMode } from '../../zustand/stores/copilot';

interface UseChatbotMessageSenderProps {
  onMessageSend?: (message: string) => void;
}

interface UseChatbotMessageSenderReturn {
  sendMessage: (message: string, forceReferral?: string) => void;
}

export const useChatbotMessageSender = ({
  onMessageSend,
}: UseChatbotMessageSenderProps): UseChatbotMessageSenderReturn => {
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const {
    isChatbotReady,
    setIsChatbotInitializing,
    setCopilotDisplayMode,
    setIsChatbotReady,
    setHasPendingChatbotMessage,
    chatbotReferral
  } = useStore(state => state.copilotSlice);
  const { selectedSite } = useStore(state => state.siteSelectorSlice);

  const sendMessage = useCallback((message: string, forceReferral?: string) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }

    if (isChatbotReady) {
      initChatbotParams(
        window?.location.href,
        forceReferral ?? chatbotReferral as string,
        undefined,
        selectedSite?.metaSiteId,
        undefined,
        BI.CHATBOT_VIEW.HELP_CENTER,
      );
      sendMessageToChatbot(trimmedMessage);
      onMessageSend?.(trimmedMessage);
      setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
    } else {
      setPendingMessage(trimmedMessage);
      setHasPendingChatbotMessage(true);
      setIsChatbotInitializing(true);
    }
  }, [
    isChatbotReady,
    onMessageSend,
    setIsChatbotInitializing,
    setCopilotDisplayMode,
    setHasPendingChatbotMessage,
    chatbotReferral,
    selectedSite,
  ]);

  useEffect(() => {
    if (isChatbotReady && pendingMessage) {
      sendMessageToChatbot(pendingMessage);
      onMessageSend?.(pendingMessage);
      setPendingMessage(null);
      setHasPendingChatbotMessage(false);
      setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
    }
  }, [
    isChatbotReady,
    pendingMessage,
    onMessageSend,
    setIsChatbotInitializing,
    setIsChatbotReady,
    setHasPendingChatbotMessage,
    setCopilotDisplayMode,
  ]);
  return {
    sendMessage
  };
};
